
import { WaypointIcon, WaypointContent, CampingSpot } from '../types';

import { BRAND } from '../constants/mapColors';
// -------------------------------------------------------
// Icon + colour helpers
// -------------------------------------------------------

export const WAYPOINT_ICONS: {
  id: WaypointIcon;
  emoji: string;
  label: string;
  defaultColor: string;
}[] = [
  { id: 'pin',       emoji: '📍', label: 'Punkt',       defaultColor: '#ef4444' },
  { id: 'camp',      emoji: '🏕️', label: 'Leir',        defaultColor: BRAND },
  { id: 'parking',   emoji: '🅿️', label: 'Parkering',   defaultColor: '#3b82f6' },
  { id: 'water',     emoji: '💧', label: 'Vann',         defaultColor: '#06b6d4' },
  { id: 'viewpoint', emoji: '🔭', label: 'Utsiktspunkt', defaultColor: '#f59e0b' },
  { id: 'danger',    emoji: '⚠️', label: 'Fare',         defaultColor: '#f97316' },
  { id: 'food',      emoji: '🍽️', label: 'Mat',          defaultColor: '#8b5cf6' },
  { id: 'star',      emoji: '⭐', label: 'Favoritt',     defaultColor: '#eab308' },
];

export function getWaypointMeta(icon: WaypointIcon) {
  return WAYPOINT_ICONS.find(w => w.id === icon) ?? WAYPOINT_ICONS[0];
}

/**
 * Map an AddSpotModal category feature to the closest waypoint icon.
 * Used when a user creates a spot and we mirror it as a private waypoint.
 */
export function spotFeatureToWaypointIcon(feature?: string): WaypointIcon {
  if (!feature) return 'pin';
  const f = feature.toUpperCase();
  if (f === 'FRICAMP' || f === 'TELTPLASS' || f === 'KOMMERSIELL_CAMPING' || f === 'GAPAHUK' || f === 'BÅLPLASS') return 'camp';
  if (f === 'UTSIKT' || f === 'ATTRAKSJON') return 'viewpoint';
  if (f === 'VANN') return 'water';
  if (f === 'PICNIC' || f === 'TOALETT') return 'food';
  if (f === 'BOM') return 'danger';
  return 'pin';
}

type SpotMappingResult = {
  spotType: 'confirmed' | 'osm_amenity' | 'coastal' | 'attraction' | 'rasteplass' | 'commercial_campsite';
  features: string[];
  suitabilityColor: string;
};

/**
 * Reverse mapping: waypoint icon → spotType + features for publishing as a Fricamp spot.
 */
export function waypointIconToSpotMapping(icon: WaypointIcon): SpotMappingResult {
  const meta = getWaypointMeta(icon);
  switch (icon) {
    case 'camp':      return { spotType: 'confirmed',   features: ['TELTPLASS'], suitabilityColor: meta.defaultColor };
    case 'viewpoint': return { spotType: 'osm_amenity', features: ['UTSIKT'],    suitabilityColor: meta.defaultColor };
    case 'water':     return { spotType: 'osm_amenity', features: ['VANN'],      suitabilityColor: meta.defaultColor };
    case 'food':      return { spotType: 'osm_amenity', features: ['PICNIC'],    suitabilityColor: meta.defaultColor };
    case 'danger':    return { spotType: 'confirmed',   features: ['BOM'],       suitabilityColor: meta.defaultColor };
    case 'parking':   return { spotType: 'osm_amenity', features: [],            suitabilityColor: meta.defaultColor };
    case 'pin':
    case 'star':
    default:          return { spotType: 'osm_amenity', features: [],            suitabilityColor: meta.defaultColor };
  }
}

/**
 * Best-effort mapping fra waypoint til spot-kategori. Bruker navn/notat-keywords først,
 * fallback til ikon. Dette gir korrekt kategori for legacy-waypoints uten spesifikt ikon.
 */
export function waypointToSpotMapping(
  name: string,
  note: string | undefined,
  icon: WaypointIcon
): SpotMappingResult {
  const text = `${name || ''} ${note || ''}`.toLowerCase();
  const has = (...keys: string[]) => keys.some(k => text.includes(k));

  if (has('badeplass', 'swim', 'beach', 'strand')) return { spotType: 'osm_amenity', features: ['BADEPLASS'], suitabilityColor: '#0ea5e9' };
  if (has('toalett', 'toilet', ' wc')) return { spotType: 'osm_amenity', features: ['TOALETT'], suitabilityColor: '#3b82f6' };
  if (has('vann', 'kilde', 'drikkevann', 'water')) return { spotType: 'osm_amenity', features: ['VANN'], suitabilityColor: '#3b82f6' };
  if (has('utsikt', 'viewpoint', 'utsiktspunkt')) return { spotType: 'osm_amenity', features: ['UTSIKT'], suitabilityColor: BRAND };
  if (has('gapahuk', 'shelter')) return { spotType: 'osm_amenity', features: ['GAPAHUK'], suitabilityColor: BRAND };
  if (has('bålplass', 'balplass', 'baal', 'fire pit', 'firepit', 'bålpanne')) return { spotType: 'osm_amenity', features: ['BÅLPLASS'], suitabilityColor: '#f97316' };
  if (has('teltplass', 'tent site')) return { spotType: 'confirmed', features: ['TELTPLASS'], suitabilityColor: BRAND };
  if (has('campingplass', 'camping') && !has('teltplass')) return { spotType: 'confirmed', features: ['TELTPLASS'], suitabilityColor: BRAND };
  if (has('bom', 'barrier', 'sperring')) return { spotType: 'confirmed', features: ['BOM'], suitabilityColor: '#dc2626' };
  if (has('picnic', 'rasteplass')) return { spotType: 'osm_amenity', features: ['PICNIC'], suitabilityColor: '#3b82f6' };
  if (has('attraksjon', 'attraction', 'sevärdighet')) return { spotType: 'attraction', features: ['ATTRAKSJON'], suitabilityColor: '#8b5cf6' };
  if (has('kai', 'brygge', 'pier', 'wharf')) return { spotType: 'coastal', features: ['KAI/BRYGGE'], suitabilityColor: '#1d4ed8' };
  if (has('vei-ende', 'veiende', 'snuplass', 'road end', 'turning')) return { spotType: 'confirmed', features: [], suitabilityColor: BRAND };

  // Fallback: bruk ikonet
  return waypointIconToSpotMapping(icon);
}

/**
 * Striper rekursive `wpcard-` og `visit_`-prefix fra en spot-id slik at vi
 * får tilbake den originale underliggende spot-ID-en. Brukes når vi skriver
 * besøk eller occupancy — vi vil ALDRI lagre `wpcard-...`/`visit_...`-IDer
 * inn som spotId fordi det skaper en uendelig synthesis-kaskade
 * (`wpcard-visit_wpcard-visit_...manual-xyz`).
 */
export function unwrapSyntheticSpotId(id: string): string {
  let s = id;
  // Loop fordi en korrupt id kan ha mange lag (tidligere bug etterlot 40+ nivåer)
  while (s.startsWith('wpcard-') || s.startsWith('visit_')) {
    if (s.startsWith('wpcard-')) s = s.slice('wpcard-'.length);
    else s = s.slice('visit_'.length);
  }
  return s;
}

/** True for IDer som kun er klient-side rendering-aliaser (visit/waypoint). */
export function isSyntheticSpotId(id: unknown): boolean {
  return typeof id === 'string' && (id.startsWith('wpcard-') || id.startsWith('visit_'));
}

/**
 * Snuplass-deteksjon på tvers av språk og kilder. Treffer norsk feature
 * `SNUPLASS`, engelsk `TURNING AREA`, navn som inneholder `SNUPLASS`/
 * `TURNING`, og OSM-tag `highway === 'turning_circle'`. Returnerer false
 * for null/undefined-spotter.
 */
export function isSnuplassSpot(spot: { name?: string; features?: string[]; highway?: string } | null | undefined): boolean {
  if (!spot) return false;
  const features = Array.isArray(spot.features) ? spot.features.map(f => String(f).toUpperCase()) : [];
  if (features.includes('SNUPLASS') || features.includes('TURNING AREA')) return true;
  const upper = (spot.name || '').toUpperCase();
  if (upper.includes('SNUPLASS') || upper.includes('TURNING')) return true;
  return spot.highway === 'turning_circle';
}

// -------------------------------------------------------
// Synthesize CampingSpot from a private waypoint, slik at det rendres
// med samme ikon, popup og kort som de offentlige lag-spottene.
// -------------------------------------------------------

// Keyword-bare mapping (uten icon-fallback). Brukes for visits der icon-feltet
// er hardkodet til 'camp' uavhengig av spot-typen som er besøkt — så icon kan
// ikke brukes som signal for kategori.
function waypointKeywordMapping(name: string, note: string | undefined): SpotMappingResult | null {
  const text = `${name || ''} ${note || ''}`.toLowerCase();
  const has = (...keys: string[]) => keys.some(k => text.includes(k));

  // Sjekk mest spesifikke først (mer eksakte termer overstyrer generelle)
  if (has('bom', 'barrier', 'sperring')) return { spotType: 'confirmed', features: ['BOM'], suitabilityColor: '#dc2626' };
  if (has('snuplass', 'turning circle')) return { spotType: 'rasteplass', features: ['SNUPLASS'], suitabilityColor: '#1565C0' };
  if (has('rasteplass', 'rest area')) return { spotType: 'rasteplass', features: ['RASTEPLASS'], suitabilityColor: '#1565C0' };
  if (has('badeplass', 'swim', 'beach', 'strand')) return { spotType: 'osm_amenity', features: ['BADEPLASS'], suitabilityColor: '#0ea5e9' };
  if (has('toalett', 'toilet', ' wc')) return { spotType: 'osm_amenity', features: ['TOALETT'], suitabilityColor: '#3b82f6' };
  if (has('drikkevann', 'water tap', 'kilde', 'vannpost')) return { spotType: 'osm_amenity', features: ['VANN'], suitabilityColor: '#3b82f6' };
  if (has('utsikt', 'viewpoint', 'utsiktspunkt', 'utkikk')) return { spotType: 'osm_amenity', features: ['UTSIKT'], suitabilityColor: BRAND };
  if (has('gapahuk', 'shelter')) return { spotType: 'osm_amenity', features: ['GAPAHUK'], suitabilityColor: BRAND };
  if (has('bålplass', 'balplass', 'baal', 'fire pit', 'firepit', 'bålpanne')) return { spotType: 'osm_amenity', features: ['BÅLPLASS'], suitabilityColor: '#f97316' };
  if (has('teltplass', 'tent site')) return { spotType: 'confirmed', features: ['TELTPLASS'], suitabilityColor: BRAND };
  if (has('fricamp', 'overnatting', 'villmarksleir')) return { spotType: 'confirmed', features: ['FRICAMP'], suitabilityColor: '#2E7D32' };
  if (has('campingplass') || (has('camping') && !has('teltplass'))) return { spotType: 'commercial_campsite', features: ['KOMMERSIELL_CAMPING'], suitabilityColor: '#1565C0' };
  if (has('parkering', 'parking', 'p-plass')) return { spotType: 'osm_amenity', features: [], suitabilityColor: '#3b82f6' };
  if (has('picnic')) return { spotType: 'osm_amenity', features: ['PICNIC'], suitabilityColor: '#3b82f6' };
  if (has('attraksjon', 'attraction', 'sevärdighet', 'severdighet')) return { spotType: 'attraction', features: ['ATTRAKSJON'], suitabilityColor: '#8b5cf6' };
  if (has('kai', 'brygge', 'pier', 'wharf')) return { spotType: 'coastal', features: ['KAI/BRYGGE'], suitabilityColor: '#1d4ed8' };
  if (has('dump station', 'dumpstasjon', 'tømmestasjon', 'tomming')) return { spotType: 'osm_amenity', features: ['DUMP_STATION'], suitabilityColor: '#8b5cf6' };
  if (has('fjelltopp', 'topp', 'peak')) return { spotType: 'osm_amenity', features: ['FJELLTOPP'], suitabilityColor: BRAND };
  // Bredeste til slutt — så "vannfall" ikke trigger "vann"
  if (has(' vann', 'water')) return { spotType: 'osm_amenity', features: ['VANN'], suitabilityColor: '#3b82f6' };
  return null;
}

/**
 * Returnerer true hvis et waypoint har nok kategori-info til å rendres som et
 * proper layer-spot (Bom, Teltplass, Vann osv.). Generiske pin/star-waypoints
 * uten matchende keywords i navnet returnerer false — de fortsetter å rendres
 * via UserContentLayer som personlige kartpunkter.
 *
 * Visits (`visit_*`) har alltid `icon: 'camp'` hardkodet uavhengig av spot-typen,
 * så for visits ignoreres icon-fallback — kun navn/notat-keywords teller.
 * Slik unngår vi at et besøk til en parkering kategoriseres som "Teltplass".
 */
export function waypointHasLayerCategory(waypoint: WaypointContent): boolean {
  if (waypoint.features && waypoint.features.length > 0) return true;
  if (waypoint.spotMetadata?.barrierMetadata) return true;
  if (waypoint.spotMetadata?.spotType && waypoint.spotMetadata.spotType !== 'osm_amenity') return true;

  const isVisit = waypoint.id.startsWith('visit_');
  if (isVisit) {
    return waypointKeywordMapping(waypoint.name, waypoint.note) !== null;
  }
  const mapping = waypointToSpotMapping(waypoint.name, waypoint.note, waypoint.icon);
  return mapping.features.length > 0;
}

/**
 * Konverterer et privat waypoint til et CampingSpot-objekt. Brukes for
 * rendering på kartet (SpotMarker) og i karusellen, og for å slå opp
 * spot-detalj-kortet via wpcard-ID.
 *
 * Persistert metadata fra AddSpotModal har høyest prioritet; faller tilbake
 * til navn/ikon-heuristikk for eldre waypoints.
 */
export function synthesizeSpotFromWaypoint(waypoint: WaypointContent): CampingSpot {
  const persistedFeatures = waypoint.features ?? [];
  const meta = waypoint.spotMetadata ?? {};
  const heuristic = waypointToSpotMapping(waypoint.name, waypoint.note, waypoint.icon);

  const features = persistedFeatures.length > 0 ? persistedFeatures : heuristic.features;
  const spotType = (meta.spotType as any) ?? heuristic.spotType;
  const suitabilityColor = meta.suitabilityColor ?? heuristic.suitabilityColor ?? waypoint.color ?? BRAND;
  const description = meta.description ?? waypoint.note ?? '';

  return {
    id: `wpcard-${waypoint.id}`,
    source: 'user',
    importVersion: 'waypoint',
    lastSyncedAt: new Date(waypoint.updatedAt || waypoint.createdAt).toISOString(),
    name: waypoint.name || 'Mitt punkt',
    description,
    location: { latitude: waypoint.latitude, longitude: waypoint.longitude },
    score: features.length > 0 ? 7 : 0,
    spotType,
    sourceLevel: 4,
    suitabilityColor,
    features: Array.from(new Set([...features, 'BRUKERBIDRAG', 'PRIVAT'])),
    reasoning: 'Privat punkt — kun synlig for deg.',
    trafficRisk: 'N/A',
    buildingProximity: 'N/A',
    nearestHouse: 999,
    highway: 'N/A',
    surfaceType: 'N/A',
    isPublicAccess: false,
    legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false },
    createdBy: waypoint.userId,
    createdAt: waypoint.createdAt,
    photoUrls: waypoint.photoUrl ? [waypoint.photoUrl] : [],
    visibility: 'private',
    barrierMetadata: meta.barrierMetadata,
    attractionCategory: meta.attractionCategory,
    website: meta.website,
    videoUrl: meta.videoUrl,
    isVerified: !!waypoint.photoUrl,
  } as CampingSpot;
}

// -------------------------------------------------------
// Share
// -------------------------------------------------------

/**
 * Deler et waypoint via Web Share API eller kopierer lenken.
 * Returnerer true hvis deling/kopiering lyktes.
 */
export async function shareWaypoint(
  waypointId: string,
  name: string,
  latitude: number,
  longitude: number
): Promise<boolean> {
  const { APP_URL } = await import('../constants');
  const params = new URLSearchParams({
    lat: latitude.toFixed(5),
    lon: longitude.toFixed(5),
    name,
  });
  const shareUrl = `${APP_URL}/share/waypoint/${waypointId}?${params.toString()}`;
  const shareText = `${name} — ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;

  if (navigator.share) {
    try {
      await navigator.share({
        title: name,
        text: shareText,
        url: shareUrl,
      });
      return true;
    } catch {
      return false;
    }
  }

  try {
    await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
    return true;
  } catch {
    return false;
  }
}
